/* eslint-disable object-shorthand */
/* eslint-disable prefer-destructuring */
import React, { useMemo } from 'react';
import { TradingPanel, TradingText } from '@trading-ui/core';
import { type QueryObserverResult, type RefetchOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { MathUtils } from '@/utils/math.utils';

import type {
  Consumption,
  ConsumptionChartResponse,
  ConsumptionTableResponse,
  GroupedConsumption,
  ConsumptionTableRow,
} from '@/interfaces';

import useAppStore from '@/store/app.store';
import useConsumerStore from '@/views/consumer_view/store/consumer.store';
import usePricingIndices from '@/views/settings_view/hooks/api/usePricingIndices';
import ConsumptionTable from '../../consumption-table/ConsumptionTable';

import * as styles from './TablePanel.css';

const getContainerStyles = (haveData: boolean): React.CSSProperties => ({
  backgroundColor: haveData ? '#F3BDFF' : 'var(--palette-background-paper)',
  height: '50px',
  borderEndEndRadius: '10px',
  borderEndStartRadius: '10px',
  boxShadow: 'var(--shadows-2)',
  display: 'flex',
  alignItems: 'center',
});

type Props = {
  tableData: ConsumptionTableResponse;
  isLoading: boolean;
  mutateChart: (options?: RefetchOptions) => Promise<QueryObserverResult<ConsumptionChartResponse, Error>>;
};

export default function TablePanel({ tableData, isLoading, mutateChart }: Props) {
  const { t } = useTranslation();
  const { pricingIndices } = usePricingIndices();

  // Get companies and masters for filtering plants
  const masters = useAppStore((state) => state.masters);
  const averageNomination = useConsumerStore((state) => state.averageNomination);
  const averageEnagas = useConsumerStore((state) => state.averageEnagas);
  const displayedPlantIds = useMemo(() => {
    if (!tableData?.consumptions?.length) return new Set<number>();

    return new Set(tableData.consumptions.map((consumption) => consumption.plant.id));
  }, [tableData?.consumptions]);

  // Build this from the loaded table data so draft filter changes don't redraw the table.
  const plantPricingIndexMap = useMemo(() => {
    const map = new Map<number, number | null>();
    masters.plants
      .filter((plant) => displayedPlantIds.has(plant.id))
      .forEach((plant) => {
        map.set(plant.id, plant.pricingIndexId);
      });
    return map;
  }, [masters.plants, displayedPlantIds]);

  const plantCompanyMap = useMemo(() => {
    const map = new Map<number, number>();
    masters.plants.forEach((p) => map.set(p.id, p.companyId));
    return map;
  }, [masters.plants]);

  // Get the set of identifiers that are duplicated (same identifier, DIFFERENT companies)
  const duplicateIdentifiers = useMemo(() => {
    if (!tableData?.consumptions?.length) return new Set<string>();

    // Group consumptions by plant identifier, tracking unique companyIds per identifier
    const identifierCompanies = new Map<string, Set<number>>();
    tableData.consumptions.forEach((consumption) => {
      const identifier = consumption.plant.identifier;
      const plantId = consumption.plant.id;
      const companyId = plantCompanyMap.get(plantId);

      if (!identifierCompanies.has(identifier)) {
        identifierCompanies.set(identifier, new Set());
      }
      if (companyId !== undefined) {
        identifierCompanies.get(identifier)!.add(companyId);
      }
    });

    // Return the set of identifiers that have more than one COMPANY
    const duplicates = new Set<string>();
    identifierCompanies.forEach((companyIds, identifier) => {
      if (companyIds.size > 1) {
        duplicates.add(identifier);
      }
    });
    return duplicates;
  }, [tableData?.consumptions, plantCompanyMap]);

  // Get list of unique plants from duplicates (for column generation)
  // Enrich with company name from masters
  const duplicatePlants = useMemo(() => {
    if (!tableData?.consumptions?.length || duplicateIdentifiers.size === 0) return [];

    // Create a map of companyId -> company name
    const companyNameMap = new Map<number, string>();
    masters.companies.forEach((c) => companyNameMap.set(c.id, c.name));

    const plantsMap = new Map<number, Consumption['plant'] & { companyName?: string; companyId?: number }>();
    tableData.consumptions.forEach((consumption) => {
      if (duplicateIdentifiers.has(consumption.plant.identifier)) {
        const companyId = plantCompanyMap.get(consumption.plant.id);
        const companyName = companyId ? companyNameMap.get(companyId) : '';
        plantsMap.set(consumption.plant.id, {
          ...consumption.plant,
          companyId: companyId || 0,
          companyName: companyName || '',
        });
      }
    });
    return Array.from(plantsMap.values()).sort((a, b) => (a.companyId || 0) - (b.companyId || 0));
  }, [tableData?.consumptions, duplicateIdentifiers, masters.companies, plantCompanyMap]);

  // Process consumptions: group duplicates into single rows, keep non-duplicates as-is
  const processedTableData = useMemo((): ConsumptionTableRow[] => {
    if (!tableData?.consumptions?.length) return [];

    const rows: ConsumptionTableRow[] = [];
    const processedGroups = new Set<string>();

    // Keep all days from the same CUPS together.
    const sortedConsumptions = [...tableData.consumptions].sort((a, b) => {
      const aliasDiff = a.plant.alias.localeCompare(b.plant.alias);

      if (aliasDiff !== 0) return aliasDiff;

      const identifierDiff = a.plant.identifier.localeCompare(b.plant.identifier);

      if (identifierDiff !== 0) return identifierDiff;

      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });

    sortedConsumptions.forEach((consumption) => {
      const identifier = consumption.plant.identifier;
      const dateStr =
        consumption.date instanceof Date
          ? consumption.date.toISOString().split('T')[0]
          : String(consumption.date).split('T')[0];
      const groupKey = `${identifier}_${dateStr}`;

      if (duplicateIdentifiers.has(identifier)) {
        if (processedGroups.has(groupKey)) return;
        processedGroups.add(groupKey);

        const groupedConsumptions = sortedConsumptions.filter((c) => {
          const cIdentifier = c.plant.identifier;
          const cDateStr = c.date instanceof Date ? c.date.toISOString().split('T')[0] : String(c.date).split('T')[0];
          return cIdentifier === identifier && cDateStr === dateStr;
        });

        const firstConsumption = groupedConsumptions[0];

        const groupedRow: GroupedConsumption = {
          isGrouped: true,
          date: firstConsumption.date,
          identifier: identifier,
          alias: firstConsumption.plant.alias,
          consumptions: groupedConsumptions,
          totalNomination: firstConsumption.totalNomination ?? null,
          totalDifference: firstConsumption.totalDifference ?? null,
          enagas: firstConsumption.enagas,
          difference: firstConsumption.difference,
        };

        groupedConsumptions.forEach((c) => {
          (groupedRow as any)[`nomination_${c.plant.id}`] = c.nomination;
        });

        rows.push(groupedRow);
      } else {
        rows.push(consumption);
      }
    });

    return rows;
  }, [tableData?.consumptions, duplicateIdentifiers]);

  return (
    <motion.div
      className={styles.root}
      initial={{ x: '-100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}>
      <TradingPanel className={styles.panel}>
        <ConsumptionTable
          processedData={processedTableData}
          isLoading={isLoading}
          mutateChart={mutateChart}
          pricingIndices={pricingIndices}
          plantPricingIndexMap={plantPricingIndexMap}
          duplicateIdentifiers={duplicateIdentifiers}
          duplicatePlants={duplicatePlants}
        />
      </TradingPanel>
      <div style={getContainerStyles(Boolean(tableData))}>
        {tableData && (
          <div className={styles.averagesContent}>
            <TradingText
              text={`${t('consumer.tablePanel.averages.nomination')}: ${MathUtils.formatNumber(
                averageNomination ?? 0,
                2,
                2,
                true
              )}`}
              variant="h6"
              style={{ fontSize: '12px', fontWeight: 'bold' }}
            />
            <TradingText
              text={`${t('consumer.tablePanel.averages.enagas')}: ${MathUtils.formatNumber(
                averageEnagas ?? 0,
                2,
                2,
                true
              )}`}
              variant="h6"
              style={{ fontSize: '12px', fontWeight: 'bold' }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}
